import React, { ChangeEvent, useState } from 'react';
import TextField from '@mui/material/Input';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import InputLabel from '@mui/material/InputLabel';

const useStyles = makeStyles(theme => ({
  inputContainer: {
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  label: {
    marginBottom: theme.spacing(1),
    color: '#797C80',
  },
  input: {
    background: '#FFFFFF',
    minHeight: '52px',
    borderRadius: '.75rem',
    boxShadow: 'none',
    boxSizing: 'border-box',
    // padding: `${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(
    //   1
    // )}`,
    padding: '0.5rem 1rem',
    width: '100%',
    '&:-webkit-autofill': {
      transition: 'background-color 5000s ease-in-out 0s',
      WebkitBoxShadow: '0 0 0 30px white inset !important',
    },
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
}));

type TextInputProps = {
  id?: string;
  name?: string;
  label?: any;
  value?: string;
  type?: string;
  placeholder?: string;
  onChangeEvent?(e: React.FormEvent<{}>): void;
  onChange?(value: string): void;
  onClick?(e: React.MouseEvent<{}>): void;
  validators?: string[];
  errorMessages?: string[];
  inputProps?: {
    [key: string]: string | number | ((e: React.FormEvent<{}>) => void) | React.ReactNode;
  };
  disabled?: boolean;
  currentPassValue?: string;
  required?: boolean;
  minRow?: number;
  maxRow?: number;
  multiline?: boolean;
};

const TextInput: React.FC<TextInputProps> = ({
  id,
  name,
  label,
  value,
  placeholder,
  type = 'text',
  onChangeEvent,
  onChange,
  onClick,
  validators = [],
  errorMessages = [],
  inputProps = {},
  currentPassValue = '',
  disabled = false,
  required = false,
  minRow = 1,
  maxRow = 1,
  multiline = false,
}) => {
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const classes = useStyles();

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value;

    if (onChangeEvent) {
      onChangeEvent(event);
    }

    if (onChange) {
      onChange(newValue);
    }

    let firstErrorIndex = -1;

    const hasError = validators.some((validator, index) => {
      if (validator === 'required') {
        if (!newValue.trim()) {
          firstErrorIndex = index;
          return true;
        }
      } else {
        const isValid = validate(validator, newValue);
        if (!isValid) {
          firstErrorIndex = index;
          return true;
        }
      }

      return false;
    });

    setError(hasError);
    setErrorMessage(hasError ? errorMessages[firstErrorIndex] : '');
  };

  const validate = (validator: string, value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidCharactersRegex = /[&!#^$%*()""~,`]/;

    switch (validator) {
      case 'required':
        return value.trim() !== '';

      case 'isValidDOB': {
        if (!value) return true; // let "required" handle empty check

        // ✅ Strict YYYY-MM-DD with 4-digit year
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (!regex.test(value)) {
          return false;
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
          return false;
        }

        const today = new Date();

        // ✅ Prevent future dates
        if (date > today) {
          return false;
        }

        // ✅ Prevent dates more than 200 years in the past
        const twoHundredYearsAgo = new Date(
          today.getFullYear() - 200,
          today.getMonth(),
          today.getDate()
        );
        if (date < twoHundredYearsAgo) {
          return false;
        }

        return true;
      }

      case 'isValidServiceDate': {
        if (!value) return true;

        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (!regex.test(value)) return false;

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return false;

        const year = date.getUTCFullYear();
        if (year < 1900 || year > 2100) return false;

        return true;
      }

      case 'isEmail':
        if (value.length === 0) return true;
        return emailRegex.test(value) && !invalidCharactersRegex.test(value);

      case 'noWhitespacePadding':
        return !/^\s|\s$/.test(value);

      case 'maxStringLength':
        return value.length <= 128;

      case 'isPasswordMatch':
        return currentPassValue === value;

      case 'validatePhoneNumberLength':
        return value.length === 10;

      case 'validateZipCode':
        return value.length === 5;

      case 'checkAgeUnder18': {
        const currentDate = new Date();
        const eighteenYearsAgo = new Date(
          currentDate.getFullYear() - 18,
          currentDate.getMonth(),
          currentDate.getDate()
        );
        const enteredDate = new Date(value);
        return enteredDate >= eighteenYearsAgo;
      }

      default:
        return true;
    }
  };

  return (
    <div className={classes.inputContainer}>
      <InputLabel className={classes.label}>{label}</InputLabel>
      <TextField
        id={id}
        type={type}
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={handleInputChange}
        onClick={onClick}
        error={error}
        classes={{ root: classes.input }}
        fullWidth
        multiline={multiline}
        minRows={minRow}
        maxRows={maxRow}
        disableUnderline
        disabled={disabled}
        required={required}
        {...inputProps}
      />
      {error && (
        <Typography variant="body2" className={classes.errorText}>
          {errorMessage}
        </Typography>
      )}
    </div>
  );
};

TextInput.defaultProps = {
  id: '',
  type: 'text',
  name: '',
  value: '',
  label: '',
  placeholder: '',
  disabled: false,
  validators: [],
  errorMessages: [],
  inputProps: {},
};

export default TextInput;
